import {
  getFirestore,
  collection,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  addDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { ROLE } from "../shared/constants";
import { parseVersionToNumber } from "../shared/utils";
import { gendersMap } from "../shared/constants";
import { shouldReSyncRole, isAccountDeactivatedError } from "./authErrors";
import { logClientError } from "../helpers/clientLogger";

export interface TherapistPatch {
  displayName: string;
  email?: string;
  currentEmail?: string;
  isAdmin?: boolean;
}

export class AdminApi {
  #user;
  #db;
  #defaultDB;
  #onUnauthorized?: () => void;
  #onAccountDeactivated?: () => void;
  #demoCredentialsCache = new Map<string, { email: string; password: string }>();

  constructor(user, firebaseApp) {
    this.#user = user;
    this.#db = getFirestore(firebaseApp, user?.tenantId);
    this.#defaultDB = getFirestore(firebaseApp);
  }
  #getApiUrl = (path) => {
    return process.env.REACT_APP_BASE_API_URL + path;
  };

  setUnauthorizedHandler = (fn: () => void) => {
    this.#onUnauthorized = fn;
  };

  setAccountDeactivatedHandler = (fn: () => void) => {
    this.#onAccountDeactivated = fn;
  };

  #throwIfNotOk = async (response: Response, path: string) => {
    if (response.ok) return;
    const errorText = await response.text();
    if (isAccountDeactivatedError(errorText)) this.#onAccountDeactivated?.();
    if (shouldReSyncRole(errorText, path)) this.#onUnauthorized?.();
    throw new Error(errorText);
  };

  #get = async (path) => {
    const idToken = this.#user && (await this.#user.getIdToken());
    const opt = {
      method: "GET",
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    };

    const apiUrl = this.#getApiUrl(path);

    const response = await fetch(apiUrl, opt);

    await this.#throwIfNotOk(response, path);
    return response.json();
  };

  #post = async (path, payload) => {
    const idToken = this.#user && (await this.#user.getIdToken());
    const isFormData = payload instanceof FormData;
    const headers: Record<string, string> = {
      Authorization: `Bearer ${idToken}`,
    };
    if (!isFormData) {
      headers["Content-Type"] =
        payload instanceof Blob
          ? "application/octet-stream"
          : "application/json";
    }
    const opt = {
      method: "POST",
      headers,
      body: isFormData
        ? payload
        : payload instanceof Blob
          ? payload
          : JSON.stringify(payload),
    };
    const apiUrl = this.#getApiUrl(path);

    const response = await fetch(apiUrl, opt);

    await this.#throwIfNotOk(response, path);
    return response.json();
  };

  #put = async (path, payload) => {
    const idToken = this.#user && (await this.#user.getIdToken());
    const isFormData = payload instanceof FormData;
    const headers: Record<string, string> = {
      Authorization: `Bearer ${idToken}`,
    };
    if (!isFormData) {
      headers["Content-Type"] =
        payload instanceof Blob
          ? "application/octet-stream"
          : "application/json";
    }
    const opt = {
      method: "PUT",
      headers,
      body: isFormData
        ? payload
        : payload instanceof Blob
          ? payload
          : JSON.stringify(payload),
    };
    const apiUrl = this.#getApiUrl(path);

    const response = await fetch(apiUrl, opt);

    await this.#throwIfNotOk(response, path);
    return response.json();
  };

  #patch = async (path, payload: any = {}) => {
    const idToken = this.#user && (await this.#user.getIdToken());
    const opt = {
      method: "PATCH",
      headers: {
        "Content-Type":
          payload instanceof Blob
            ? "application/octet-stream"
            : "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: payload instanceof Blob ? payload : JSON.stringify(payload),
    };
    const apiUrl = this.#getApiUrl(path);

    const response = await fetch(apiUrl, opt);

    await this.#throwIfNotOk(response, path);
    return response.json();
  };

  #delete = async (path) => {
    const idToken = this.#user && (await this.#user.getIdToken());
    const opt = {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    };
    const apiUrl = this.#getApiUrl(path);
    const response = await fetch(apiUrl, opt);

    await this.#throwIfNotOk(response, path);
    return response.json();
  };

  setAppConfigsToEveryone = () => {
    return this.#post("/api/set_app_configs_for_all_tenants", {});
  };

  setAppConfigsToTenant = (tenantId) => {
    return this.#post("/api/set_app_configs_for_one_tenant", { tenantId });
  };

  getRole = () => {
    return this.#get("/api/role");
  };

  requestUserTenants = async (email, origin) => {
    return this.#post(`/api/request-user-tenants`, {
      email,
      origin,
      roles: [ROLE.TENANT_ADMIN, ROLE.TENANT_SUPER_ADMIN, ROLE.SUPER_ADMIN],
    });
  };

  setTherapistStatus = (tenantId, uid, status) => {
    return this.#put(`/api/therapist/${tenantId}/${uid}`, { status });
  };

  deleteTherapist = (tenantId, uid) => {
    return this.#delete(`/api/tenants/${tenantId}/therapist/${uid}`);
  };

  changeTherapistStatus = (tenantId, therapistId, status) => {
    return this.#put(`/api/tenants/${tenantId}/therapists/${therapistId}`, {
      status,
    });
  };

  setTherapistPrivileges = (tenantId, therapistId, currentEmail?: string) => {
    return this.#put(`/api/tenants/${tenantId}/therapists/${therapistId}`, {
      role: ROLE.TENANT_SUPER_ADMIN,
      ...(currentEmail ? { currentEmail } : {}),
    });
  };

  revokeTherapistPrivileges = (tenantId, therapistId, currentEmail?: string) => {
    return this.#put(`/api/tenants/${tenantId}/therapists/${therapistId}`, {
      role: ROLE.TENANT_ADMIN,
      ...(currentEmail ? { currentEmail } : {}),
    });
  };

  updateTherapist = (
    tenantId: string,
    therapistId: string,
    patch: TherapistPatch
  ) => {
    return this.#patch(
      `/api/tenants/${tenantId}/therapists/${therapistId}`,
      patch
    );
  };

  changeTherapistTimezone = (tenantId, therapistId, timezone) => {
    return this.#put(`/api/tenants/${tenantId}/therapists/${therapistId}`, {
      timezone,
    });
  };

  inviteTherapist = (
    tenantId: string,
    input: { fullName: string; email: string; isPracticeAdmin: boolean }
  ) => {
    const origin = window.location.origin;
    return this.#post(`/api/tenants/${tenantId}/invite-therapist`, {
      ...input,
      origin,
    });
  };

  resendInvitation = (tenantId, email) => {
    const origin = window.location.origin;
    return this.#post(`/api/tenants/${tenantId}/resend-invitation`, {
      email,
      origin,
    });
  };

  deleteInvitation = (tenantId, email) => {
    return this.#post(`/api/tenants/${tenantId}/delete-invitation`, { email });
  };

  grabInvitation = (tenantId, email) => {
    return this.#post(`/api/tenants/${tenantId}/grab-invitation`, { email });
  };

  verifyInvitation = async (invitation) => {
    const origin = window.location.origin;
    return this.#post("/api/verify-invitation", { invitation, origin });
  };

  getTenants = ({ skipLog = false } = {}) => {
    return this.#get(`/api/tenants${skipLog ? "?skipLog=true" : ""}`);
  };

  getTenant = (tenantId) => {
    return this.#get(`/api/tenants/${tenantId}`);
  };

  createTenant = ({
    name,
    logoFullFile,
    logoSquareFile,
  }: {
    name: string;
    logoFullFile?: File;
    logoSquareFile?: File;
  }) => {
    const formData = new FormData();
    formData.append("name", name);
    if (logoFullFile) formData.append("logoFull", logoFullFile);
    if (logoSquareFile) formData.append("logoSquare", logoSquareFile);
    return this.#post("/api/tenants", formData);
  };

  updateTenant = (tenantId, tenantData) => {
    return this.#put(`/api/tenants/${tenantId}`, tenantData);
  };

  deleteTenant = (tenantId: string) => {
    return this.#delete(`/api/tenants/${tenantId}`);
  };

  updateTenantPractice = ({
    tenantId,
    displayName,
    logoFullFile,
    logoSquareFile,
    deleteLogoFull,
    deleteLogoSquare,
  }: {
    tenantId: string;
    displayName: string;
    logoFullFile?: File;
    logoSquareFile?: File;
    deleteLogoFull?: boolean;
    deleteLogoSquare?: boolean;
  }) => {
    const formData = new FormData();
    formData.append("displayName", displayName);
    if (logoFullFile) formData.append("logoFull", logoFullFile);
    if (logoSquareFile) formData.append("logoSquare", logoSquareFile);
    if (deleteLogoFull) formData.append("deleteLogoFull", "true");
    if (deleteLogoSquare) formData.append("deleteLogoSquare", "true");
    return this.#put(`/api/tenants/${tenantId}`, formData);
  };

  uploadPracticeLogo = (
    tenantId: string,
    kind: "full" | "square",
    file: File
  ) => {
    const formData = new FormData();
    formData.append(kind === "full" ? "logoFull" : "logoSquare", file);
    return this.#put(`/api/tenants/${tenantId}`, formData);
  };

  removePracticeLogo = (tenantId: string, kind: "full" | "square") => {
    const formData = new FormData();
    formData.append(
      kind === "full" ? "deleteLogoFull" : "deleteLogoSquare",
      "true"
    );
    return this.#put(`/api/tenants/${tenantId}`, formData);
  };

  getTherapists = (tenantId, { skipLog = false } = {}) => {
    return this.#get(
      `/api/tenants/${tenantId}/therapists${skipLog ? "?skipLog=true" : ""}`
    );
  };

  // `fresh` is the page-open signal: the server then serves its live-today cache
  // only if it is seconds old, instead of up to 15 minutes. Independent of
  // `skipLog`, which answers a different question (does this count as activity).
  getDashboard = (tenantId, period, { skipLog = false, fresh = false } = {}) => {
    return this.#get(
      `/api/tenants/${tenantId}/dashboard?period=${period}${
        skipLog ? "&skipLog=true" : ""
      }${fresh ? "&fresh=true" : ""}`
    );
  };

  getInvitations = (tenantId, { skipLog = false } = {}) => {
    return this.#get(
      `/api/tenants/${tenantId}/invitations${skipLog ? "?skipLog=true" : ""}`
    );
  };

  getClients = async (
    tenantId,
    _onUpdate?: (clients: any[]) => void,
    { skipLog = false } = {}
  ) => {
    const skipLogParam = skipLog ? "?skipLog=true" : "";
    // Backend `GET /clients` now returns each client already enriched with
    // `uplifeAgreements` and `hasHiddenContent` (see functions/backend/utils.js
    // -> getClientDetails). The list page no longer needs per-client getDoc
    // round-trips or onSnapshot subscriptions, which previously blocked the
    // first paint on the Firestore WebSocket handshake.
    //
    // Real-time updates of agreements / hidden-content on the LIST view are
    // delivered via the 30s poll in ClientsList. Per-client subscriptions
    // (`subscribeToClientAgreements`, `subscribeToUserHasHiddenContent`) are
    // still available for the client-detail page through clientStore.
    const clients = await this.#get(
      `/api/tenants/${tenantId}/clients${skipLogParam}`
    );
    return {
      clients,
      unsubscribe: () => {},
    };
  };

  inviteClient = (tenantId: string, email: string, fullName?: string) => {
    const origin = window.location.origin;
    return this.#post(`/api/tenants/${tenantId}/invite-client`, {
      ...(fullName ? { fullName } : {}),
      email,
      origin,
    });
  };

  getPracticeClients = (tenantId: string) => {
    return this.#get(`/api/tenants/${tenantId}/clients`);
  };

  inviteClientByPracticeAdmin = (
    tenantId: string,
    fullName: string,
    email: string,
    therapists: { id: string; role: 'primary' | 'managing' }[] = []
  ) => {
    const origin = window.location.origin;
    return this.#post(`/api/tenants/${tenantId}/invite-client`, {
      fullName,
      email,
      origin,
      therapists,
    });
  };

  updateClientTherapists = (
    tenantId: string,
    clientId: string,
    therapists: { id: string; role: 'primary' | 'managing' }[]
  ) => {
    return this.#put(`/api/tenants/${tenantId}/clients/${clientId}/therapists`, {
      therapists,
    });
  };

  sendClientLogs = async (events: unknown[]) => {
    await this.#post(`/api/client-logs`, { events });
  };

  unassignSelfFromClient = async (tenantId: string, clientId: string) => {
    await this.#post(
      `/api/tenants/${tenantId}/clients/${clientId}/unassign-self`,
      {}
    );
  };

  updateClientDisplayName = async (
    tenantId: string,
    clientId: string,
    displayName: string
  ) => {
    await this.#put(`/api/tenants/${tenantId}/clients/${clientId}`, {
      displayName,
    });
  };

  getSingleInteractivities = async (
    locale,
    gender,
    version,
    isStandalone = false
  ) => {
    const interactivitiesCollectionRef = collection(
      this.#defaultDB,
      "single_interactivities"
    );
    const versionNumber = parseVersionToNumber(version);

    const genderField = gendersMap[`${gender}`];

    const filters = [
      where(`filters.locales.${locale}`, "==", true),
      where(`filters.profileGender.${genderField}`, "==", true),
      where("ritual_setup_supported", "==", true),
    ];

    if (isStandalone) {
      filters.push(where("sessionId", "==", null));
    } else {
      filters.push(where("min_app_version_number", "<=", versionNumber));
    }

    const q = query(interactivitiesCollectionRef, ...filters);

    const interactivitiesSnapshot = await getDocs(q);
    return interactivitiesSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  };

  getSingleListenings = async (locale, gender, version) => {
    const interactivitiesCollectionRef = collection(
      this.#defaultDB,
      "single_interactivities"
    );
    const versionNumber = parseVersionToNumber(version);
    const normalizedGender = gendersMap[`${gender}`];

    const filters = [
      where("content.type", "==", "listening"),
      where(`filters.locales.${locale}`, "==", true),
      where(`filters.profileGender.${normalizedGender}`, "==", true),
      where("min_app_version_number", "<=", versionNumber),
      where("is_featured", "==", true),
    ];

    const q = query(interactivitiesCollectionRef, ...filters);

    const interactivitiesSnapshot = await getDocs(q);
    return interactivitiesSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  };

  getSingles = async (locale, gender, version) => {
    const interactivitiesCollectionRef = collection(
      this.#defaultDB,
      "sessions"
    );
    const versionNumber = parseVersionToNumber(version);
    const normalizedGender = gendersMap[`${gender}`];

    const filters = [
      where(`filters.locales.${locale}`, "==", true),
      where(`filters.profileGender.${normalizedGender}`, "==", true),
      where("min_app_version_number", "<=", versionNumber),
      where("is_single_and_featured", "==", true),
    ];

    const q = query(interactivitiesCollectionRef, ...filters);

    const interactivitiesSnapshot = await getDocs(q);
    return interactivitiesSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  };

  getCategories = async () => {
    const categoriesCollectionRef = collection(this.#defaultDB, "categories");
    const categoriesSnapshot = await getDocs(categoriesCollectionRef);
    return categoriesSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  };

  getProtocols = async (gender, locale, minVersion) => {
    const protocolsCollectionRef = collection(
      this.#defaultDB,
      "therapy_protocols"
    );

    const minVersionNumber = parseVersionToNumber(minVersion);
    const genderField = gendersMap[`${gender}`];

    const q = query(
      protocolsCollectionRef,
      where(`filters.locales.${locale}`, "==", true),
      where(`filters.profileGender.${genderField}`, "==", true),
      where("min_app_version_number", "<=", minVersionNumber)
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  };

  setProtocols = async (protocolsObject) => {
    const protocolsRef = doc(this.#defaultDB, "protocols", "data");
    await setDoc(protocolsRef, protocolsObject, { merge: false });
  };

  sendResetPasswordEmail = (email, tenantId) => {
    const origin = window.location.origin;
    return this.#post(`/api/reset-password`, {
      email,
      tenantId,
      origin,
    });
  };

  changeClientStatus = (tenantId, clientId, status) => {
    return this.#put(`/api/tenants/${tenantId}/clients/${clientId}`, {
      status,
    });
  };

  deleteClient = (tenantId, clientId) => {
    return this.#delete(`/api/tenants/${tenantId}/clients/${clientId}`);
  };

  grabClient = (tenantId, clientId) => {
    return this.#patch(`/api/tenants/${tenantId}/clients/${clientId}/grab`);
  };

  refuseClient = (tenantId, clientId) => {
    return this.#patch(`/api/tenants/${tenantId}/clients/${clientId}/refuse`);
  };

  getClientAuth = async (tenantId, clientId) => {
    return await this.#get(`/api/tenants/${tenantId}/clients/${clientId}`);
  };

  // Demo app sign-in credentials, cached per client (creds are stable; the page's
  // 30s poll naturally refreshes). The endpoint returns {email,password} on 200,
  // or a plain-string body on 400 (auth) / 404 (not a demo) — #get rejects on any
  // non-200, so the caller can render an empty state (UP-2911).
  getDemoCredentials = async (
    tenantId: string,
    clientId: string
  ): Promise<{ email: string; password: string }> => {
    if (!this.#user) throw new Error("Not authenticated");
    const key = `${tenantId}:${clientId}`;
    const cached = this.#demoCredentialsCache.get(key);
    if (cached) return cached;
    const creds = (await this.#get(
      `/api/tenants/${tenantId}/clients/${clientId}/demo-credentials`
    )) as { email: string; password: string };
    this.#demoCredentialsCache.set(key, creds);
    return creds;
  };

  getClientProfile = async (uid) => {
    const profileRef = doc(this.#db, `/users/${uid}/static/profile`);
    const profileSnapshot = await getDoc(profileRef);
    return profileSnapshot.data();
  };

  subscribeToClientProfile = (uid, callback) => {
    const profileRef = doc(this.#db, `/users/${uid}/static/profile`);
    return onSnapshot(profileRef, (snapshot) => {
      callback(snapshot.data());
    });
  };

  getClientFavorites = async (uid) => {
    const favoritesRef = doc(
      this.#db,
      `/users/${uid}/static/favorites_content_items_snapshot`
    );
    const favoritesSnapshot = await getDoc(favoritesRef);
    return favoritesSnapshot.data();
  };

  getClientAgreements = async (uid) => {
    const agreementsRef = doc(this.#db, `/users/${uid}/static/agreements`);
    const agreementsSnapshot = await getDoc(agreementsRef);
    return agreementsSnapshot.data();
  };

  subscribeToClientAgreements = (uid, callback) => {
    const agreementsRef = doc(this.#db, `/users/${uid}/static/agreements`);
    return onSnapshot(agreementsRef, (snapshot) => {
      callback(snapshot.data());
    });
  };

  // Determine if user has any hidden content (forbiddances)
  hasHiddenContentFromRules = (rules) => {
    if (!rules || !rules.content || !rules.content.forbiddances) return false;

    const isTrue = (entry) => {
      if (!entry) return false;
      if (Object.prototype.hasOwnProperty.call(entry, "value")) {
        if (entry.value === true) return true;
      }
      return Object.values(entry).some((v) => isTrue(v));
    };

    const forb = rules.content.forbiddances;
    const checkGroup = (group) => {
      if (!group) return false;
      return isTrue(group.specifics) || isTrue(group.categories);
    };

    return (
      checkGroup(forb.journeys) ||
      checkGroup(forb.interactivities) ||
      checkGroup(forb.sessions)
    );
  };

  // Single read: returns boolean, does not create default config
  getUserHasHiddenContent = async (uid) => {
    const rulesRef = doc(this.#db, `/users/${uid}/configs/tenant_config`);
    const rulesSnapshot = await getDoc(rulesRef);
    if (!rulesSnapshot.exists()) return false;
    const rules = rulesSnapshot.data();
    return this.hasHiddenContentFromRules(rules);
  };

  // Live subscription: emits boolean on each change
  subscribeToUserHasHiddenContent = (uid, callback) => {
    const rulesRef = doc(this.#db, `/users/${uid}/configs/tenant_config`);
    return onSnapshot(rulesRef, (snapshot) => {
      const rules = snapshot.data();
      callback(this.hasHiddenContentFromRules(rules));
    });
  };

  getClientMoodScreening = async (uid) => {
    const moodScreeningCollectionRef = collection(
      this.#db,
      `users/${uid}/mood_screenings`
    );
    const moodScreeningDocs = await getDocs(moodScreeningCollectionRef);
    return moodScreeningDocs.docs
      .map((doc) => doc.data())
      .sort((a, b) => b.lastUpdatedAt.seconds - a.lastUpdatedAt.seconds);
  };

  getMoodTrackingSnapshot = async (uid) => {
    const moodTrackingSnapshotRef = doc(
      this.#db,
      `users/${uid}/static/mood_tracking_snapshot`
    );
    const moodTrackingSnapshot = await getDoc(moodTrackingSnapshotRef);
    return moodTrackingSnapshot.data();
  };

  subscribeToMoodTrackingSnapshot = (uid, callback) => {
    const moodTrackingSnapshotRef = doc(
      this.#db,
      `users/${uid}/static/mood_tracking_snapshot`
    );
    return onSnapshot(moodTrackingSnapshotRef, (snapshot) => {
      callback(snapshot.data());
    });
  };

  getActivityStatisticsSnapshot = async (uid) => {
    const activityStatisticsSnapshotRef = doc(
      this.#db,
      `users/${uid}/static/activity_statistic_snapshot`
    );
    const activityStatisticsSnapshot = await getDoc(
      activityStatisticsSnapshotRef
    );
    return activityStatisticsSnapshot.data();
  };

  subscribeToActivityStatisticsSnapshot = (uid, callback) => {
    const activityStatisticsSnapshotRef = doc(
      this.#db,
      `users/${uid}/static/activity_statistic_snapshot`
    );
    return onSnapshot(activityStatisticsSnapshotRef, (snapshot) => {
      callback(snapshot.data());
    });
  };

  getTestsData = async (uid) => {
    const testsCollectionRef = collection(this.#db, `users/${uid}/calendar`);
    const q = query(testsCollectionRef, where("id_isStandalone", "==", true));
    const testsSnapshot = await getDocs(q);
    return testsSnapshot.docs.map((doc) => doc.data());
  };

  subscribeToTestsData = (uid, callback) => {
    const testsCollectionRef = collection(this.#db, `users/${uid}/calendar`);
    const q = query(testsCollectionRef, where("id_isStandalone", "==", true));
    return onSnapshot(q, (snapshot) => {
      callback(snapshot.docs.map((doc) => doc.data()));
    });
  };

  getClientCalendar = async (uid) => {
    const calendarCollectionRef = collection(this.#db, `users/${uid}/calendar`);
    const calendarDocs = await getDocs(calendarCollectionRef);
    return calendarDocs.docs.map((doc) => doc.data());
  };

  getClientDiary = async (uid) => {
    const diaryCollectionRef = collection(this.#db, `users/${uid}/diary`);
    const diaryDocs = await getDocs(diaryCollectionRef);
    return diaryDocs.docs.map((doc) => doc.data());
  };

  setClientAgreements = async (uid, agreements) => {
    const agreementsRef = doc(this.#db, `/users/${uid}/static/agreements`);
    await setDoc(agreementsRef, agreements);
  };

  signUpWithToken = async (
    token,
    password,
    displayName,
    agree,
    preferences
  ) => {
    return this.#post("/api/sign-up-with-token", {
      token,
      password,
      displayName,
      agree,
      preferences,
    });
  };

  markAsRegistered = async (email) => {};

  requestResetPassword = (email, tenantId) => {
    const origin = window.location.origin;
    return this.#post("/api/reset-password", { email, tenantId, origin });
  };

  subscribeToClientMoodScreening = (uid, callback) => {
    const moodScreeningCollectionRef = collection(
      this.#db,
      `users/${uid}/mood_screenings`
    );
    return onSnapshot(moodScreeningCollectionRef, (snapshot) => {
      const data = snapshot.docs
        .map((doc) => doc.data())
        .sort((a, b) => b.lastUpdatedAt.seconds - a.lastUpdatedAt.seconds);
      callback(data);
    });
  };

  subscribeToClientCalendar = (uid, callback) => {
    const calendarCollectionRef = collection(this.#db, `users/${uid}/calendar`);
    return onSnapshot(calendarCollectionRef, (snapshot) => {
      const data = snapshot.docs.map((doc) => doc.data());
      callback(data);
    });
  };

  subscribeToClientDiary = (uid, callback) => {
    const diaryCollectionRef = collection(this.#db, `users/${uid}/diary`);
    return onSnapshot(diaryCollectionRef, (snapshot) => {
      const data = snapshot.docs.map((doc) => doc.data());
      callback(data);
    });
  };

  subscribeToClientFavorites = (uid, callback) => {
    const favoritesRef = doc(
      this.#db,
      `/users/${uid}/static/favorites_content_items_snapshot`
    );
    return onSnapshot(favoritesRef, (snapshot) => {
      callback(snapshot.data());
    });
  };

  updateClientRitualFields = async (uid, ritualId, data, morning, evening) => {
    const profileRef = doc(this.#db, `/users/${uid}/static/profile`);
    const profileSnapshot = await getDoc(profileRef);
    const profileData = profileSnapshot.data();
    const ritualsItems = profileData?.ritualsInfo?.items;
    if (!ritualsItems[ritualId] || !ritualsItems[ritualId].i)
      throw new Error("Ritual not found");

    const activeIteration = Object.entries(ritualsItems[ritualId].i).find(
      ([_, item]: [string, any]) => item.st === 0
    );

    if (!activeIteration) throw new Error("Active iteration not found");
    const [iterationId] = activeIteration;
    if (
      !profileData?.ritualsInfo?.morning ||
      !profileData?.ritualsInfo?.evening
    ) {
      await updateDoc(profileRef, {
        [`ritualsInfo.evening`]: evening,
        [`ritualsInfo.morning`]: morning,
      });
    }
    await updateDoc(profileRef, {
      [`ritualsInfo.items.${ritualId}.i.${iterationId}.p`]: data,
    });
  };

  deleteClientRitual = async (uid, ritualId) => {
    const profileRef = doc(this.#db, `/users/${uid}/static/profile`);
    const profileSnapshot = await getDoc(profileRef);
    const profileData = profileSnapshot.data();
    const ritualData = profileData?.ritualsInfo?.items?.[ritualId];

    if (ritualData?.i) {
      const activeIteration = Object.entries(ritualData.i).find(
        ([_, item]: [string, any]) => item.st === 0
      );
      if (activeIteration) {
        const [iterationId] = activeIteration;
        await updateDoc(profileRef, {
          [`ritualsInfo.items.${ritualId}.i.${iterationId}.st`]: -1,
        });
      }
    }
  };

  addClientRitual = async (uid, ritualId, ritualData, morning, evening) => {
    const profileRef = doc(this.#db, `/users/${uid}/static/profile`);
    const profileSnapshot = await getDoc(profileRef);
    const profileData = profileSnapshot.data();

    const existingItem = profileData?.ritualsInfo?.items?.[ritualId];
    const updatePayload = {};

    if (!profileData?.ritualsInfo?.morning && morning) {
      updatePayload[`ritualsInfo.morning`] = morning;
    }
    if (!profileData?.ritualsInfo?.evening && evening) {
      updatePayload[`ritualsInfo.evening`] = evening;
    }

    if (!existingItem) {
      updatePayload[`ritualsInfo.items.${ritualId}`] = ritualData;
      await updateDoc(profileRef, updatePayload);
      return;
    }

    const existingIterations = Object.entries(
      existingItem?.i || ({} as Record<string, any>)
    );
    const maxExisting = existingIterations.reduce(
      (max, [key, val]: [string, any]) => {
        const byValue = typeof val?.i === "number" ? val.i : 0;
        const byKey = Number(key.split("_").pop()) || 0;
        const candidate = Math.max(byValue, byKey);
        return candidate > max ? candidate : max;
      },
      0
    );
    const nextIteration = maxExisting + 1;
    const newIterationKey = `${ritualId}_${nextIteration}`;

    const providedEntry =
      (Object.values(ritualData?.i || ({} as Record<string, any>))[0] as any) ||
      {};
    const newIterationEntry = { ...providedEntry, i: nextIteration };

    if (!(existingItem as any)?.c && (ritualData as any)?.c) {
      updatePayload[`ritualsInfo.items.${ritualId}.c`] = (ritualData as any).c;
    }

    updatePayload[`ritualsInfo.items.${ritualId}.i.${newIterationKey}`] =
      newIterationEntry;

    await updateDoc(profileRef, updatePayload);
  };

  addClientRitualsBatch = async (uid, ritualsMap, morning, evening) => {
    const profileRef = doc(this.#db, `/users/${uid}/static/profile`);
    const profileSnapshot = await getDoc(profileRef);
    const profileData = profileSnapshot.data();

    const updatePayload = {};

    Object.entries(ritualsMap || {}).forEach(
      ([ritualId, ritualData]: [string, any]) => {
        const existingItem = profileData?.ritualsInfo?.items?.[ritualId];

        if (!existingItem) {
          updatePayload[`ritualsInfo.items.${ritualId}`] = ritualData;
          return;
        }

        const existingIterations = Object.entries(
          existingItem?.i || ({} as Record<string, any>)
        );
        const maxExisting = existingIterations.reduce(
          (max, [key, val]: [string, any]) => {
            const byValue = typeof val?.i === "number" ? val.i : 0;
            const byKey = Number(key.split("_").pop()) || 0;
            const candidate = Math.max(byValue, byKey);
            return candidate > max ? candidate : max;
          },
          0
        );
        const nextIteration = maxExisting + 1;
        const newIterationKey = `${ritualId}_${nextIteration}`;

        const providedEntry =
          (Object.values(
            ritualData?.i || ({} as Record<string, any>)
          )[0] as any) || {};
        const newIterationEntry = { ...providedEntry, i: nextIteration };

        if (!(existingItem as any)?.c && (ritualData as any)?.c) {
          updatePayload[`ritualsInfo.items.${ritualId}.c`] = (
            ritualData as any
          ).c;
        }

        updatePayload[`ritualsInfo.items.${ritualId}.i.${newIterationKey}`] =
          newIterationEntry;
      }
    );

    if (!profileData?.ritualsInfo?.morning && morning) {
      updatePayload[`ritualsInfo.morning`] = morning;
    }
    if (!profileData?.ritualsInfo?.evening && evening) {
      updatePayload[`ritualsInfo.evening`] = evening;
    }

    if (Object.keys(updatePayload).length === 0) return;
    await updateDoc(profileRef, updatePayload);
  };

  addClientJourney = async (uid, journeyId, journeyData) => {
    const profileRef = doc(this.#db, `/users/${uid}/static/profile`);

    await updateDoc(profileRef, {
      [`progress.journeys.${journeyId}`]: journeyData,
    });
  };

  updateClientJourney = async (
    uid,
    journeyId,
    time,
    shouldUpdateOnlyTime = false
  ) => {
    const profileRef = doc(this.#db, `/users/${uid}/static/profile`);

    if (shouldUpdateOnlyTime) {
      await updateDoc(profileRef, {
        [`progress.journeys.${journeyId}.t`]: time,
      });
    } else {
      const profileSnapshot = await getDoc(profileRef);
      const profileData = profileSnapshot.data();

      const journeyData = profileData?.progress?.journeys?.[journeyId];
      const activeIteration = Object.entries(journeyData.i).find(
        ([_, item]: [string, any]) => item.st === 1
      );
      const [iterationId] = activeIteration!;

      await updateDoc(profileRef, {
        [`ritualsInfo.practice`]: time,
        [`progress.journeys.${journeyId}.i.${iterationId}.lEdit_at`]:
          Timestamp.now(),
        [`progress.journeys.${journeyId}.i.${iterationId}.lEdit_by`]:
          "therapist",
      });
    }
  };

  resumeClientJourney = async (uid, journeyId) => {
    const profileRef = doc(this.#db, `/users/${uid}/static/profile`);
    const profileSnapshot = await getDoc(profileRef);
    const profileData = profileSnapshot.data();

    const journeyData = profileData?.progress?.journeys?.[journeyId];

    if (journeyData?.i) {
      const activeIteration = Object.entries(journeyData.i).find(
        ([_, item]: [string, any]) => item.st === 0
      );

      if (activeIteration) {
        const [iterationId] = activeIteration;
        await updateDoc(profileRef, {
          [`progress.journeys.${journeyId}.i.${iterationId}.st`]: 1,
          [`progress.journeys.${journeyId}.i.${iterationId}.lEdit_at`]:
            Timestamp.now(),
          [`progress.journeys.${journeyId}.i.${iterationId}.lEdit_by`]:
            "therapist",
          [`progress.journeys.${journeyId}.i.${iterationId}.init`]: "therapist",
        });
      }
    }
  };

  deleteClientJourney = async (uid, journeyId) => {
    const profileRef = doc(this.#db, `/users/${uid}/static/profile`);
    const profileSnapshot = await getDoc(profileRef);
    const profileData = profileSnapshot.data();

    const journeyData = profileData?.progress?.journeys?.[journeyId];

    if (journeyData?.i) {
      const activeIteration = Object.entries(journeyData.i).find(
        ([_, item]: [string, any]) => item.st === 1
      );

      if (activeIteration) {
        const [iterationId] = activeIteration;
        await updateDoc(profileRef, {
          [`progress.journeys.${journeyId}.i.${iterationId}.st`]: 0,
        });
      }
    }
  };

  updateClientRitualTime = async (uid, group, time, rituals) => {
    const profileRef = doc(this.#db, `/users/${uid}/static/profile`);

    if (group === "custom" && rituals) {
      const updateData = {};

      rituals.forEach((ritual) => {
        if (ritual.i) {
          const activeIteration = Object.entries(ritual.i).find(
            ([_, item]: [string, any]) => item.st === 0
          );

          if (activeIteration) {
            const [iterationId] = activeIteration;
            updateData[
              `ritualsInfo.items.${ritual.id}.i.${iterationId}.p.time.time`
            ] = time;
          }
        }
      });

      if (Object.keys(updateData).length > 0) {
        await updateDoc(profileRef, updateData);
      }
    } else {
      await updateDoc(profileRef, {
        [`ritualsInfo.${group}`]: time,
      });
    }
  };

  setClientMoodScreeningTime = async (uid, time) => {
    const profileRef = doc(this.#db, `/users/${uid}/static/profile`);
    await updateDoc(profileRef, {
      [`ritualsInfo.moodScreening`]: time,
    });
  };

  toggleJourneyCategoryRecommendation = async (uid, categoryId, value) => {
    const rulesRef = doc(this.#db, `/users/${uid}/configs/tenant_config`);
    const rulesSnapshot = await getDoc(rulesRef);
    const rulesData = rulesSnapshot.data();

    const recommendations =
      rulesData?.content?.recommendations?.journeys?.specifics || {};

    const categories = await this.getCategories();
    const category = categories.find(
      (cat: any) => cat.id === categoryId
    ) as any;
    const categoryJourneyIds = category
      ? Object.keys(category.journeys || {})
      : [];

    const patches: Array<{ path: string; value: any }> = [
      {
        path: `content.forbiddances.journeys.categories.${categoryId}`,
        value,
      },
    ];

    if (value) {
      Object.entries(recommendations).forEach(
        ([journeyId, recommendation]: [string, any]) => {
          if (
            recommendation?.value === true &&
            categoryJourneyIds.includes(journeyId)
          ) {
            patches.push({
              path: `content.recommendations.journeys.specifics.${journeyId}`,
              value: false,
            });
          }
        }
      );
    }

    await this.#post("/api/update_user_app_config", {
      userId: uid,
      patches,
    });
  };

  toggleJourneyForbidden = async (uid, journeyId, value) => {
    const rulesRef = doc(this.#db, `/users/${uid}/configs/tenant_config`);
    const rulesSnapshot = await getDoc(rulesRef);
    const rulesData = rulesSnapshot.data();

    const recommendations =
      rulesData?.content?.recommendations?.journeys?.specifics || {};

    const patches = [
      {
        path: `content.forbiddances.journeys.specifics.${journeyId}`,
        value,
      },
    ];

    if (recommendations[journeyId]?.value) {
      patches.push({
        path: `content.recommendations.journeys.specifics.${journeyId}`,
        value: false,
      });
    }

    await this.#post("/api/update_user_app_config", {
      userId: uid,
      patches,
    });
  };

  toggleListeningForbidden = async (uid, listeningId, value) => {
    // const rulesRef = doc(this.#db, `/users/${uid}/configs/tenant_config`);
    // const rulesSnapshot = await getDoc(rulesRef);
    // const rulesData = rulesSnapshot.data();

    // We don't use recommendations for meditations for now
    // const recommendations =
    //   rulesData?.content?.recommendations?.meditations?.specifics || {};

    const patches = [
      {
        path: `content.forbiddances.interactivities.specifics.${listeningId}`,
        value,
      },
    ];

    // if (recommendations[listeningId]?.value) {
    //   patches.push({
    //     path: `content.recommendations.meditations.specifics.${listeningId}`,
    //     value: false,
    //   });
    // }

    await this.#post("/api/update_user_app_config", {
      userId: uid,
      patches,
    });
  };

  toggleSingleForbidden = async (uid, singleId, value) => {
    // const rulesRef = doc(this.#db, `/users/${uid}/configs/tenant_config`);
    // const rulesSnapshot = await getDoc(rulesRef);
    // const rulesData = rulesSnapshot.data();

    // const recommendations =
    //   rulesData?.content?.recommendations?.singles?.specifics || {};

    const patches = [
      {
        path: `content.forbiddances.sessions.specifics.${singleId}`,
        value,
      },
    ];

    // if (recommendations[singleId]?.value) {
    //   patches.push({
    //     path: `content.recommendations.singles.specifics.${singleId}`,
    //     value: false,
    //   });
    // }

    await this.#post("/api/update_user_app_config", {
      userId: uid,
      patches,
    });
  };

  toggleSessionForbidden = async (uid, sessionId, value) => {
    const patches = [
      {
        path: `content.forbiddances.sessions.specifics.${sessionId}`,
        value,
      },
    ];

    await this.#post("/api/update_user_app_config", {
      userId: uid,
      patches,
    });
  };

  toggleJourneyRecommended = async (uid, journeyId, value) => {
    const patches = [
      {
        path: `content.recommendations.journeys.specifics.${journeyId}`,
        value,
      },
    ];

    await this.#post("/api/update_user_app_config", {
      userId: uid,
      patches,
    });
  };

  toggleMeditationRecommended = async (uid, meditationId, value) => {
    const patches = [
      {
        path: `content.recommendations.meditations.specifics.${meditationId}`,
        value,
      },
    ];

    await this.#post("/api/update_user_app_config", {
      userId: uid,
      patches,
    });
  };

  toggleSingleRecommended = async (uid, singleId, value) => {
    const patches = [
      {
        path: `content.recommendations.singles.specifics.${singleId}`,
        value,
      },
    ];

    await this.#post("/api/update_user_app_config", {
      userId: uid,
      patches,
    });
  };

  clearAllRecommendations = async (uid) => {
    const rulesRef = doc(this.#db, `/users/${uid}/configs/tenant_config`);
    const rulesSnapshot = await getDoc(rulesRef);
    const rulesData = rulesSnapshot.data();

    const specifics =
      rulesData?.content?.recommendations?.journeys?.specifics || {};

    const patches = Object.keys(specifics).map((journeyId) => ({
      path: `content.recommendations.journeys.specifics.${journeyId}`,
      value: false,
    }));

    await this.#post("/api/update_user_app_config", {
      userId: uid,
      patches,
    });
  };

  clearAllRecommendationsByActivity = async (uid, activityKey) => {
    const rulesRef = doc(this.#db, `/users/${uid}/configs/tenant_config`);
    const rulesSnapshot = await getDoc(rulesRef);
    const rulesData = rulesSnapshot.data();

    const specifics =
      rulesData?.content?.recommendations?.[activityKey]?.specifics || {};

    const patches = Object.keys(specifics).map((itemId) => ({
      path: `content.recommendations.${activityKey}.specifics.${itemId}`,
      value: false,
    }));

    if (patches.length === 0) return;

    await this.#post("/api/update_user_app_config", {
      userId: uid,
      patches,
    });
  };

  resetUserContentConfig = async (uid) => {
    const rulesRef = doc(this.#db, `/users/${uid}/configs/tenant_config`);
    const rulesSnapshot = await getDoc(rulesRef);
    const rulesData = rulesSnapshot.data() || {};

    const patches: Array<{ path: string; value: any }> = [];
    // Reset journeys recommendations
    const recommendationSpecifics =
      rulesData?.content?.recommendations?.journeys?.specifics || {};
    Object.keys(recommendationSpecifics).forEach((journeyId) => {
      if (recommendationSpecifics[journeyId]?.value !== false) {
        patches.push({
          path: `content.recommendations.journeys.specifics.${journeyId}`,
          value: false,
        });
      }
    });

    const forbiddenJourneySpecifics =
      rulesData?.content?.forbiddances?.journeys?.specifics || {};
    Object.keys(forbiddenJourneySpecifics).forEach((journeyId) => {
      if (forbiddenJourneySpecifics[journeyId]?.value !== false) {
        patches.push({
          path: `content.forbiddances.journeys.specifics.${journeyId}`,
          value: false,
        });
      }
    });

    // Reset journeys categories forbiddances
    const forbiddenJourneyCategories =
      rulesData?.content?.forbiddances?.journeys?.categories || {};
    Object.keys(forbiddenJourneyCategories).forEach((categoryId) => {
      if (forbiddenJourneyCategories[categoryId]?.value !== false) {
        patches.push({
          path: `content.forbiddances.journeys.categories.${categoryId}`,
          value: false,
        });
      }
    });

    // Reset meditations forbiddances as well
    const forbiddenInteractivitySpecifics =
      rulesData?.content?.forbiddances?.interactivities?.specifics || {};
    // Reset singles forbiddances as well
    const forbiddenSinglesSpecifics =
      rulesData?.content?.forbiddances?.sessions?.specifics || {};
    Object.keys(forbiddenSinglesSpecifics).forEach((singleId) => {
      if (forbiddenSinglesSpecifics[singleId]?.value !== false) {
        patches.push({
          path: `content.forbiddances.singles.specifics.${singleId}`,
          value: false,
        });
      }
    });
    Object.keys(forbiddenInteractivitySpecifics).forEach((meditationId) => {
      if (forbiddenInteractivitySpecifics[meditationId]?.value !== false) {
        patches.push({
          path: `content.forbiddances.interactivities.specifics.${meditationId}`,
          value: false,
        });
      }
    });

    const forbiddenSessionSpecifics =
      rulesData?.content?.forbiddances?.sessions?.specifics || {};
    Object.keys(forbiddenSessionSpecifics).forEach((sessionId) => {
      if (forbiddenSessionSpecifics[sessionId]?.value !== false) {
        patches.push({
          path: `content.forbiddances.sessions.specifics.${sessionId}`,
          value: false,
        });
      }
    });

    if (patches.length === 0) {
      return;
    }

    await this.#post("/api/update_user_app_config", {
      userId: uid,
      patches,
    });
  };

  resetClientContentSettings = async (uid: string) => {
    await this.#post("/api/reset_user_content_app_config", { userId: uid });
  };

  getUserTenantConfig = async (uid) => {
    const rulesRef = doc(this.#db, `/users/${uid}/configs/tenant_config`);
    const rulesSnapshot = await getDoc(rulesRef);

    if (!rulesSnapshot.exists()) {
      const defaultRules = {};
      await setDoc(rulesRef, defaultRules);
      return defaultRules;
    }

    return rulesSnapshot.data();
  };

  subscribeToUserTenantConfig = (uid, callback) => {
    const rulesRef = doc(this.#db, `/users/${uid}/configs/tenant_config`);
    return onSnapshot(rulesRef, (snapshot) => {
      callback(snapshot.data());
    });
  };

  getTherapistConfig = async (therapistId) => {
    const rulesRef = doc(
      this.#db,
      `/therapists/${therapistId}/configs/app_config`
    );
    const rulesSnapshot = await getDoc(rulesRef);

    if (!rulesSnapshot.exists()) {
      const defaultRules = {};
      await setDoc(rulesRef, defaultRules);
      return defaultRules;
    }

    return rulesSnapshot.data();
  };

  subscribeToTherapistConfig = (therapistId, callback) => {
    const rulesRef = doc(
      this.#db,
      `/therapists/${therapistId}/configs/app_config`
    );
    return onSnapshot(rulesRef, (snapshot) => {
      callback(snapshot.data());
    });
  };

  subscribeToTherapistConfigOverrides = (therapistId: string, callback: (config: any) => void) => {
    const ref = doc(this.#db, `/therapists/${therapistId}/configs/app_config_overrides_only`);
    return onSnapshot(ref, (snap) => callback(snap.data() ?? {}));
  };

  updateInitialPersonalPlan = async (
    mode: "protocol" | "personalize" | "none",
    protocolId: string | null,
    scope: "tenant" | "therapist",
    tenantId?: string
  ): Promise<{ config: unknown }> => {
    return this.#post("/api/update_initial_personal_plan", { mode, protocolId, scope, ...(tenantId ? { tenantId } : {}) });
  };

  resetTherapistClientAppConfig = async (): Promise<void> => {
    await this.#post("/api/reset_therapist_client_app_config", {});
  };

  getInitialPlanProtocolOptions = async (): Promise<Array<{ id: string; title: string; locale: string }>> => {
    const snap = await getDocs(collection(this.#defaultDB, "therapy_protocols"));
    return snap.docs.map((d) => ({ id: d.id, title: d.data().title, locale: d.data().locale }));
  };

  toggleTherapistConfig = async (path, value) => {
    const patches = [
      {
        path,
        value,
      },
    ];

    await this.#post("/api/update_therapist_app_config", {
      patches,
    });
  };

  updateTherapistConfigPatches = async (patches) => {
    await this.#post("/api/update_therapist_app_config", {
      patches,
    });
  };

  getTenantConfig = async () => {
    const ref = doc(this.#db, "configs/app_config");
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, {});
      return {};
    }
    return snap.data();
  };

  subscribeToTenantConfig = (callback: (config: any | undefined) => void) => {
    if (!this.#db) return () => {};
    const ref = doc(this.#db, "configs/app_config");
    return onSnapshot(ref, (snap) => callback(snap.data() ?? {}));
  };

  getTenantConfigById = async (tenantId: string): Promise<unknown> => {
    const result = await this.#get(`/api/tenants/${tenantId}/app_config`);
    return result?.config ?? null;
  };

  toggleTenantConfig = async (
    path: string,
    value: boolean,
    tenantId?: string
  ): Promise<unknown> => {
    const body: Record<string, unknown> = { patches: [{ path, value }] };
    if (tenantId) body.tenantId = tenantId;
    return this.#post("/api/update_tenant_app_config", body);
  };

  updateTenantConfigPatches = async (
    patches: Array<{ path: string; value: unknown }>,
    tenantId?: string
  ): Promise<unknown> => {
    const body: Record<string, unknown> = { patches };
    if (tenantId) body.tenantId = tenantId;
    return this.#post("/api/update_tenant_app_config", body);
  };

  resetTenantContentSettings = async (tenantId?: string): Promise<unknown> => {
    const body: Record<string, unknown> = {};
    if (tenantId) body.tenantId = tenantId;
    return this.#post("/api/reset_tenant_content_app_config", body);
  };

  resetTherapistContentSettings = async (): Promise<void> => {
    await this.#post("/api/reset_therapist_content_app_config", {});
  };

  getJourneyById = async (journeyId) => {
    const journeyRef = doc(this.#defaultDB, "sessions", journeyId);
    const journeySnapshot = await getDoc(journeyRef);

    return journeySnapshot.data();
  };

  getAllMeditations = async () => {
    const q = query(
      collection(this.#defaultDB, "single_interactivities"),
      where("content.type", "==", "listening"),
      where("is_featured", "==", true)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  };

  getAllSingles = async () => {
    const q = query(
      collection(this.#defaultDB, "sessions"),
      where("is_single_and_featured", "==", true)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  };

  __logUserActivity = async (therapistUid, role, activity) => {
    try {
      const logItem = {
        activity,
        role,
        timestamp: Timestamp.now(),
      };

      const logsRef = doc(this.#db, `/activity-logs/${therapistUid}`);
      const logsSnapshot = await getDoc(logsRef);

      // exists is a METHOD on the modular SDK's snapshot — the bare property
      // is always truthy, so the create branch below was dead and the very
      // first write went down updateDoc, which throws on a missing doc (masked
      // in practice by the backend logger middleware creating the doc first).
      if (!logsSnapshot.exists()) {
        await setDoc(logsRef, {
          lastActivity: logItem.activity,
          lastActivityTime: logItem.timestamp,
        });
      } else {
        await updateDoc(logsRef, {
          lastActivity: logItem.activity,
          lastActivityTime: logItem.timestamp,
        });
      }

      const logsCollection = collection(
        this.#db,
        `/activity-logs/${therapistUid}/logs`
      );
      await addDoc(logsCollection, logItem);
    } catch (error) {
      console.error(error);
      logClientError(error, { surface: "activity-log", severity: "warn" });
    }
  };
}
